import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../hooks/useNotifications';

interface NotificationPromptProps {
  show: boolean;
  onDismiss: () => void;
  onEnable: () => void;
}

const NotificationPrompt: React.FC<NotificationPromptProps> = ({ 
  show, 
  onDismiss, 
  onEnable 
}) => {
  const { notificationState, requestPermission } = useNotifications();
  const [isRequesting, setIsRequesting] = useState(false);

  const handleEnable = async () => {
    setIsRequesting(true);
    try {
      const permission = await requestPermission();
      if (permission === 'granted') {
        onEnable();
      } else {
        onDismiss();
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      onDismiss();
    } finally {
      setIsRequesting(false);
    }
  };

  // Auto-dismiss if notifications are already granted
  useEffect(() => {
    if (notificationState.permission === 'granted') {
      onDismiss();
    }
  }, [notificationState.permission, onDismiss]);

  if (!show || !notificationState.supported || notificationState.permission === 'granted') {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <span className="text-2xl">🔔</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Enable Query Notifications?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Your queries can take several minutes to complete. Get notified with a sound when they're done, 
              even if you're using other tabs or applications.
            </p>
            
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-xs text-gray-500 dark:text-gray-400">✓</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Only notifies when you're away from this tab
              </span>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleEnable}
                disabled={isRequesting}
                className={`
                  flex-1 px-4 py-2 text-sm font-medium rounded-md text-white
                  ${isRequesting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-primary-dark active:bg-primary-darker'
                  }
                `}
              >
                {isRequesting ? 'Enabling...' : 'Enable Notifications'}
              </button>
              <button
                onClick={onDismiss}
                disabled={isRequesting}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Maybe Later
              </button>
            </div>
            
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              You can change this setting anytime in the app.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
